<script setup lang="ts">
import { convertKwToHp } from '@lib/shared';
import lodashCompact from 'lodash-es/compact';
import lodashToNumber from 'lodash-es/toNumber';

import { AssetEuroNorm, type SaleAssetBusEntity } from '~/apiClient';
import {
    type AssetSpecItem,
    type AssetSpecsGroup,
    type AssetSpecsSection,
    type AssetStateOfUseItem,
    type SaleStateOfUse,
} from '~/components/Sdp/SdpSpecsSection.vue';
import type { PlatformSaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
    assetId?: string;
}

const { sale, assetId } = defineProps<Props>();

// COMPOSABLES
const { tt, formatUnits, td } = useTypedI18n();
const { locale } = useTypedI18n();
const { isBundleMono } = useSaleType(() => sale.type);
const { getLocalizedCountry } = useCountries();
const { getPlatformBusClassesLabel } = usePlatformBusClass();
const { formatFirstRegistration } = useAssetRegistration();

// DATA
const sortedAssets = computed(() => sortSaleAssets(sale));
const asset = computed(() => (assetId ? sale.assets.find(_a => _a.id === assetId)! : sale.mainAsset));

const inspectionNote = computed<string | undefined>(() => {
    if (asset.value.showBadgeInspectionExpired) return tt('asset.inspectionNotes.expired');

    if (asset.value.showBadgeInspectionExpiringSoon) return tt('asset.inspectionNotes.expiringSoon');

    return undefined;
});

const mergedNotes = computed(() => {
    const documentCountry = getLocalizedCountry(asset.value.registrationDocumentCountry);
    const additionalNotes = asset.value.additionalNotes[locale.value];
    const splitAdditionalNotes = additionalNotes ? additionalNotes.split('\n') : [];

    return [
        ...splitAdditionalNotes,
        ...(inspectionNote.value ? [inspectionNote.value] : []),
        ...sortVehicleRemarks(asset.value.vehicleRemarks).map(remark => tt(`asset.vehicleRemarks.${remark}`, { documentCountry })),
    ];
});

const formattedLocation = computed(() => {
    const { address } = asset.value;

    if (!address) return null;

    if (address.fullAddress) return address.fullAddress;

    return getLocalizedCountry(address.countryCode) ?? address.country;
});

const bundleSpecs = computed<AssetSpecsSection>(() =>
    sortedAssets.value.map(_a => [
        ['asset.firstRegistration', formatFirstRegistration(_a)],
        ['asset.kilometers', formatUnits.km(_a.km)],
        ['asset.vehicleCondition.base', tt(`asset.vehicleCondition.${_a.vehicleCondition}`)],
        ...insertIf<AssetSpecItem>(!!_a.useType, () => ['asset.useType.base', tt(`asset.useType.${_a.useType!}`)]),
        ['asset.vin', _a.vin],
        // A vehicle to be registered has never been inspected: no date, so the row drops out entirely.
        ['asset.inspectionExpiryDate', _a.inspectionExpiryDate ? td(_a.inspectionExpiryDate, 'mmyyyyUTC') : null],
        ['asset.registrationDocumentCountry', getLocalizedCountry(_a.registrationDocumentCountry)],
        ...insertIf<AssetSpecItem>(!!_a.certifiedEngineMaintenance, ['asset.certifiedEngineMaintenance', _a.certifiedEngineMaintenance]),
        ...insertIf<AssetSpecItem>(!!_a.certifiedEngineMaintenance, () => [
            'asset.engineMaintenanceMileage',
            formatUnits.km(_a.engineMaintenanceMileage),
        ]),
        ...insertIf<AssetSpecItem>(!!_a.certifiedGearboxMaintenance, ['asset.certifiedGearboxMaintenance', _a.certifiedGearboxMaintenance]),
        ...insertIf<AssetSpecItem>(!!_a.certifiedGearboxMaintenance, () => [
            'asset.gearboxMaintenanceMileage',
            formatUnits.km(_a.gearboxMaintenanceMileage),
        ]),
    ])
);

const generalInformationSpecs = computed<AssetSpecsSection>(() => [
    ...insertIf<AssetSpecsGroup>(
        isBundleMono.value,
        [
            ...insertIf<AssetSpecItem>(!!asset.value.euroNorm, () => ['asset.antiPollution', tt(`asset.euroNorm.${asset.value.euroNorm!}`)]),
            ...insertIf<AssetSpecItem>(!!asset.value.class, () => ['asset.class.base', getPlatformBusClassesLabel(asset.value.class!)]),
            ['asset.location', formattedLocation.value],
        ],
        [
            ['asset.converter', asset.value.bodyBuilder],
            ['asset.deckType.base', tt(`asset.deckType.${asset.value.deckType}`)],
            ['asset.length', formatUnits.meters(asset.value.length)],
            ['asset.width', formatUnits.meters(asset.value.width)],
            ['asset.height', formatUnits.meters(asset.value.height)],
        ]
    ),
    ...insertIf<AssetSpecsGroup>(
        !isBundleMono.value,
        [
            ['asset.firstRegistration', formatFirstRegistration(asset.value)],
            ['asset.kilometers', formatUnits.km(asset.value.km)],
            ...insertIf<AssetSpecItem>(!!asset.value.euroNorm, () => ['asset.antiPollution', tt(`asset.euroNorm.${asset.value.euroNorm!}`)]),
            ...insertIf<AssetSpecItem>(!!asset.value.type, () => ['asset.busType.base', tt(`asset.busType.${asset.value.type!}`)]),
            ...insertIf<AssetSpecItem>(!!asset.value.class, () => ['asset.class.base', getPlatformBusClassesLabel(asset.value.class!)]),
            ['asset.location', formattedLocation.value],
        ],
        [
            ['asset.vehicleCondition.base', tt(`asset.vehicleCondition.${asset.value.vehicleCondition}`)],
            ...insertIf<AssetSpecItem>(!!asset.value.useType, () => ['asset.useType.base', tt(`asset.useType.${asset.value.useType!}`)]),
            ['asset.vin', asset.value.vin],
            ['asset.converter', asset.value.bodyBuilder],
            ['asset.deckType.base', tt(`asset.deckType.${asset.value.deckType}`)],
            ['asset.length', formatUnits.meters(asset.value.length)],
            ['asset.width', formatUnits.meters(asset.value.width)],
            ['asset.height', formatUnits.meters(asset.value.height)],
            ...insertIf<AssetSpecItem>(asset.value.doubleDecker, ['asset.doubleDecker', asset.value.doubleDecker]),
            ['asset.inspectionExpiryDate', asset.value.inspectionExpiryDate ? td(asset.value.inspectionExpiryDate, 'mmyyyyUTC') : null],
            ['asset.registrationDocumentCountry', getLocalizedCountry(asset.value.registrationDocumentCountry)],
        ]
    ),
    [
        ['asset.totalCapacity', asset.value.totalCapacity],
        ['asset.seats', asset.value.seats],
        ['asset.standingPlaces', asset.value.standingPlaces],
        ['asset.companionSeats', asset.value.companionSeats],
        ['asset.seatSpacing', formatUnits.cm(asset.value.seatSpacing)],
        ['asset.foldingSeats', asset.value.foldingSeats],
        ['asset.wheelchairPlaces', asset.value.wheelchairPlaces],
    ],
    [
        ['asset.seatsFromS1', asset.value.seatsFromS1],
        ['asset.standingPlacesFromS2', asset.value.standingPlacesFromS2],
    ],
    [
        ['asset.doors.base', tt(`asset.doors.${asset.value.doors}`)],
        ...insertIf<AssetSpecItem>(!!asset.value.wheelchairRamp, () => [
            'asset.wheelchairRamp.base',
            tt(`asset.wheelchairRamp.${asset.value.wheelchairRamp!}`),
        ]),
        ['asset.wheelchairRampWorking', asset.value.wheelchairRampWorking],
    ],
    omitFalseValues([
        ['asset.luggageCapacity', formatUnits.cubicMeters(lodashToNumber(asset.value.luggageCapacity))],
        ['asset.passThroughLuggageCompartment', asset.value.passThroughLuggageCompartment],
    ]),
]);

const gearboxSpecs = computed<AssetSpecsSection>(() => [
    [
        ...insertIf<AssetSpecItem>(!!asset.value.gearboxType, () => [
            'asset.gearbox.gearboxType.base',
            tt(`asset.gearbox.gearboxType.${asset.value.gearboxType!}`),
        ]),
        ['asset.gearbox.brand', asset.value.gearboxBrand],
        ['asset.gearbox.model', asset.value.gearboxModel],
    ],
    ...insertIf<AssetSpecsGroup>(!isBundleMono.value && !!asset.value.certifiedGearboxMaintenance, [
        ['asset.certifiedGearboxMaintenance', asset.value.certifiedGearboxMaintenance],
        ['asset.gearboxMaintenanceMileage', formatUnits.km(asset.value.gearboxMaintenanceMileage)],
    ]),
]);

const gearboxStateOfUse = computed<SaleStateOfUse>(() => {
    const getGearboxStateOfUse = (_a: SaleAssetBusEntity): AssetStateOfUseItem | undefined => {
        if (_a.gearboxPresent === false) return { level: 'missing_part', description: tt('asset.gearbox.missing') };

        if (_a.stateOfUseGearbox) return { level: _a.stateOfUseGearbox, description: tt(`asset.gearbox.stateOfUse.${_a.stateOfUseGearbox}`) };

        return undefined;
    };

    return getSaleStateOfUse(getGearboxStateOfUse);
});

const engineSpecs = computed<AssetSpecsSection>(() => {
    const isAdBlueVisible = asset.value.euroNorm === AssetEuroNorm.Euro5 && asset.value.adBlue !== null;

    return [
        [
            ['asset.fuel.base', tt(`asset.fuel.${asset.value.fuel}`)],
            ['asset.engine.brand', asset.value.engineBrand],
            ['asset.engine.model', asset.value.engineModel],
            ['asset.engine.position.base', tt(`asset.engine.position.${asset.value.enginePosition}`)],
        ],
        [
            ['asset.displacement', formatUnits.cc(asset.value.displacement)],
            ['asset.power', `${formatUnits.kw(asset.value.power)} (${formatUnits.hp(convertKwToHp(asset.value.power))})`],
        ],
        [
            ['asset.fuelTankCapacity', formatUnits.liters(asset.value.fuelTankCapacity)],
            ['asset.cngTanks', asset.value.cngTanks],
            ...insertIf<AssetSpecItem>(!!asset.value.cngTanksMaterial, () => [
                'asset.cngTanksMaterial.base',
                tt(`asset.cngTanksMaterial.${asset.value.cngTanksMaterial!}`),
            ]),
            ...insertIf<AssetSpecItem>(!!asset.value.cngTanksMaterial, () => [
                'asset.cngTanksMaterial.base',
                tt(`asset.cngTanksMaterial.${asset.value.cngTanksMaterial!}`),
            ]),
            ['asset.batteryPower', formatUnits.kwh(asset.value.batteryPower)],
            ...insertIf<AssetSpecItem>(!!asset.value.chargingType, () => [
                'asset.chargingType.base',
                tt(`asset.chargingType.${asset.value.chargingType!}`),
            ]),
            ...insertIf<AssetSpecItem>(!!asset.value.batteryManufacturer, () => [
                'asset.batteryManufacturer.base',
                tt(`asset.batteryManufacturer.${asset.value.batteryManufacturer!}`),
            ]),
            ...insertIf<AssetSpecItem>(!!asset.value.batteryType, () => [
                'asset.batteryType.base',
                tt(`asset.batteryType.${asset.value.batteryType!}`),
            ]),
            ...insertIf<AssetSpecItem>(!!asset.value.plugType, () => ['asset.plugType.base', tt(`asset.plugType.${asset.value.plugType!}`)]),
            ['asset.batteryChargerStationIncluded', asset.value.batteryChargerStationIncluded],
            ...insertIf<AssetSpecItem>(isAdBlueVisible, () => ['asset.adBlue', asset.value.adBlue]),
        ],
        ...insertIf<AssetSpecsGroup>(!isBundleMono.value && !!asset.value.certifiedEngineMaintenance, [
            ['asset.certifiedEngineMaintenance', asset.value.certifiedEngineMaintenance],
            ['asset.engineMaintenanceMileage', formatUnits.km(asset.value.engineMaintenanceMileage)],
        ]),
    ];
});

const engineStateOfUse = computed<SaleStateOfUse>(() => {
    const getEngineStateOfUse = (_a: SaleAssetBusEntity): AssetStateOfUseItem | undefined => {
        if (_a.enginePresent === false) return { level: 'missing_part', description: tt('asset.engine.missing') };

        if (_a.stateOfUseEngine) return { level: _a.stateOfUseEngine, description: tt(`asset.engine.stateOfUse.${_a.stateOfUseEngine}`) };

        return undefined;
    };

    return getSaleStateOfUse(getEngineStateOfUse);
});

const tyresSpecs = computed<AssetSpecsSection>(() => [
    omitFalseValues([
        ['asset.axles', asset.value.axles],
        ['asset.wheels', asset.value.wheels],
        ['asset.wheelsSizeAxle1', asset.value.wheelsSizeAxle1],
        ['asset.wheelsSizeAxle2', asset.value.wheelsSizeAxle2],
        ['asset.wheelsSizeAxle3', asset.value.wheelsSizeAxle3],
        ['asset.spareWheel', asset.value.spareWheel],
        ['asset.alloyWheels', asset.value.alloyWheels],
    ]),
]);

const tyresStateOfUse = computed<SaleStateOfUse>(() => {
    const getTyresStateOfUse = (_a: SaleAssetBusEntity): AssetStateOfUseItem | undefined =>
        _a.stateOfUseTyres ? { level: _a.stateOfUseTyres, description: tt(`asset.tyres.stateOfUse.${_a.stateOfUseTyres}`) } : undefined;

    return getSaleStateOfUse(getTyresStateOfUse);
});

const compressedAirSpecs = computed<AssetSpecsSection>(() => []);

const compressedAirStateOfUse = computed<SaleStateOfUse>(() => {
    const getCompressedAirStateOfUse = (_a: SaleAssetBusEntity): AssetStateOfUseItem | undefined =>
        _a.stateOfUseCompressedAirSystem
            ? {
                  level: _a.stateOfUseCompressedAirSystem,
                  description: tt(`asset.compressedAirSystem.stateOfUse.${_a.stateOfUseCompressedAirSystem}`),
              }
            : undefined;

    return getSaleStateOfUse(getCompressedAirStateOfUse);
});

const airConditionerSpecs = computed<AssetSpecsSection>(() => [
    [
        ['asset.heater', asset.value.heater],
        ['asset.acPassengers', asset.value.acPassengers],
        ...omitFalseValues([['asset.preHeater', asset.value.preHeater]]),
    ],
]);

const airConditionerStateOfUse = computed<SaleStateOfUse>(() => {
    const getAirConditionerStateOfUse = (_a: SaleAssetBusEntity): AssetStateOfUseItem | undefined =>
        _a.stateOfUseAirConditioner
            ? {
                  level: _a.stateOfUseAirConditioner,
                  description: tt(`asset.airConditioner.stateOfUse.${_a.stateOfUseAirConditioner}`),
              }
            : undefined;

    return getSaleStateOfUse(getAirConditionerStateOfUse);
});

const structureBodySpecs = computed<AssetSpecsSection>(() => [
    omitFalseValues([
        ['asset.indicatorSigns', asset.value.indicatorSigns],
        ['asset.indicatorSignsBrand', asset.value.indicatorSignsBrand],
    ]),
    omitFalseValues([
        ['asset.firePreventionSystem', asset.value.firePreventionSystem],
        ['asset.firePreventionSystemBrand', asset.value.firePreventionSystemBrand],
    ]),
]);

const structureBodyStateOfUse = computed<SaleStateOfUse>(() => {
    const getStructureBodyStateOfUse = (_a: SaleAssetBusEntity): AssetStateOfUseItem | undefined =>
        _a.stateOfUseStructureAndBody
            ? {
                  level: _a.stateOfUseStructureAndBody,
                  description: tt(`asset.structureBody.stateOfUse.${_a.stateOfUseStructureAndBody}`),
              }
            : undefined;

    return getSaleStateOfUse(getStructureBodyStateOfUse);
});

const interiorsSpecs = computed<AssetSpecsSection>(() => [
    omitFalseValues([
        ...insertIf<AssetSpecItem>(!!asset.value.seatUpholstery, () => [
            'asset.seatUpholstery.base',
            tt(`asset.seatUpholstery.${asset.value.seatUpholstery!}`),
        ]),
        ['asset.recliningSeats', asset.value.recliningSeats],
        ['asset.extendableSeats', asset.value.extendableSeats],
        ['asset.safetyBelts', asset.value.safetyBelts],
        ['asset.hatBox', asset.value.hatBox],
    ]),
    omitFalseValues([
        ['asset.coffeeTables', asset.value.coffeeTables],
        ['asset.seatPocket', asset.value.seatPocket],
        ['asset.armRest', asset.value.armRest],
        ['asset.footRest', asset.value.footRest],
    ]),
]);

const interiorsStateOfUse = computed<SaleStateOfUse>(() => {
    const getInteriorsStateOfUse = (_a: SaleAssetBusEntity): AssetStateOfUseItem | undefined =>
        _a.stateOfUseInteriors
            ? {
                  level: _a.stateOfUseInteriors,
                  description: tt(`asset.interiors.stateOfUse.${_a.stateOfUseInteriors}`),
              }
            : undefined;

    return getSaleStateOfUse(getInteriorsStateOfUse);
});

const accessoriesSpecs = computed<AssetSpecsSection>(() => [
    [
        ['asset.abs', asset.value.abs],
        ...omitFalseValues([
            ['asset.cruiseControl.base', tt(`asset.cruiseControl.${asset.value.cruiseControl}`)],
            ['asset.tachograph.base', tt(`asset.tachograph.${asset.value.tachograph}`)],
            ['asset.asr', asset.value.asr],
            ...insertIf<AssetSpecItem>(!!asset.value.retarder, () => ['asset.retarder.base', tt(`asset.retarder.${asset.value.retarder!}`)]),
            ['asset.breathalyzer', asset.value.breathalyzer],
        ]),
    ],
    omitFalseValues([
        ['asset.cctv', asset.value.cctv],
        ['asset.internalCameras', asset.value.internalCameras],
        ['asset.externalCameras', asset.value.externalCameras],
        ['asset.retrocamera', asset.value.retrocamera],
    ]),
    omitFalseValues([
        ['asset.driverBerth', asset.value.driverBerth],
        ['asset.socket220', asset.value.socket220],
    ]),
    omitFalseValues([
        ['asset.microphone', asset.value.microphone],
        ['asset.cdPlayer', asset.value.cdPlayer],
        ['asset.dvdPlayer', asset.value.dvdPlayer],
        ['asset.monitors', asset.value.monitors],
        ['asset.radio', asset.value.radio],
    ]),
    omitFalseValues([
        ['asset.seatsMonitor', asset.value.seatsMonitor],
        ['asset.seatsRadio', asset.value.seatsRadio],
        ['asset.seatsUsbPorts', asset.value.seatsUsbPorts],
        ['asset.seatsSocket220', asset.value.seatsSocket220],
    ]),
    omitFalseValues([
        ['asset.doubleGlazedGlass', asset.value.doubleGlazedGlass],
        ['asset.openableWindows', asset.value.openableWindows],
        ['asset.curtains', asset.value.curtains],
    ]),
    omitFalseValues([
        ['asset.kitchen', asset.value.kitchen],
        ['asset.coffeeMachine', asset.value.coffeeMachine],
        ['asset.refrigerator', asset.value.refrigerator],
        ['asset.toilet', asset.value.toilet],
    ]),
]);

// METHODS
const getSaleStateOfUse = (fn: (_a: SaleAssetBusEntity) => AssetStateOfUseItem | undefined): SaleStateOfUse =>
    isBundleMono.value ? lodashCompact(sortedAssets.value.map(fn)) : fn(asset.value);

const omitFalseValues = (items: AssetSpecItem[]): AssetSpecItem[] => items.filter(([, value]) => !!value);
</script>

<template>
    <section v-if="asset" class="flex flex-col gap-6">
        <div class="flex flex-col gap-6">
            <h3 class="text-highlighted text-2xl font-semibold md:text-3xl">
                <template v-if="isBundleMono">{{ tt('asset.bundleSpecs') }}</template>
                <template v-else>{{ tt('asset.vehicleSpecs') }}</template>
            </h3>

            <ul v-if="mergedNotes.length" class="list-inside list-disc p-0">
                <li v-for="(note, index) in mergedNotes" :key="index">{{ note }}</li>
            </ul>
        </div>

        <div>
            <SdpSpecsSection v-if="isBundleMono" :title="tt('asset.vehiclesInBundle')" :specs="bundleSpecs" default-open grouped-by-asset />

            <SdpSpecsSection :title="tt('asset.generalInformation')" :specs="generalInformationSpecs" default-open />

            <SdpSpecsSection :title="tt('asset.engine.base')" :state-of-use="engineStateOfUse" default-open :specs="engineSpecs" />

            <SdpSpecsSection :title="tt('asset.gearbox.base')" :specs="gearboxSpecs" default-open :state-of-use="gearboxStateOfUse" />

            <SdpSpecsSection :title="tt('asset.tyres.base')" :specs="tyresSpecs" default-open :state-of-use="tyresStateOfUse" />

            <SdpSpecsSection
                :title="tt('asset.compressedAirSystem.base')"
                default-open
                :specs="compressedAirSpecs"
                :state-of-use="compressedAirStateOfUse"
            />

            <SdpSpecsSection
                :title="tt('asset.airConditioner.base')"
                default-open
                :specs="airConditionerSpecs"
                :state-of-use="airConditionerStateOfUse"
            />

            <SdpSpecsSection
                :title="tt('asset.structureBody.base')"
                default-open
                :specs="structureBodySpecs"
                :state-of-use="structureBodyStateOfUse"
            />

            <SdpSpecsSection :title="tt('asset.interiors.base')" default-open :specs="interiorsSpecs" :state-of-use="interiorsStateOfUse" />

            <SdpSpecsSection :title="tt('asset.accessories')" default-open :specs="accessoriesSpecs" />
        </div>
    </section>
</template>
